/**
 * useFirestoreScenarios Hook
 *
 * Real-time listener for user scenarios from Firestore.
 * Automatically updates when scenario data changes.
 *
 * @example
 * ```typescript
 * const { scenarios, loading, error } = useFirestoreScenarios(userId);
 * ```
 */

import { useMemo } from "react";
import { useFirestoreCollection } from "./useFirestoreCollection";
import type { Scenario } from "../types/scenario";
import type { FirestoreConstraint } from "../lib/firestoreClient";

interface FirestoreScenarioDoc {
  id: string;
  userId: string;
  name: string;
  description: string;
  settings: Scenario["settings"];
  createdAt: string;
  updatedAt: string;
}

interface UseFirestoredScenariosResult {
  /** Scenario list sorted by most recent first */
  scenarios: Scenario[];
  /** Loading state */
  loading: boolean;
  /** Error state */
  error: unknown;
  /** Whether the listener is active */
  listening: boolean;
}

const SCENARIOS_CONSTRAINTS: FirestoreConstraint[] = [
  { type: "orderBy", field: "updatedAt", direction: "desc" },
];

/**
 * Hook to listen to user scenarios in real-time
 *
 * Sorted by updatedAt descending (most recent first)
 *
 * @param userId - User ID
 * @param enabled - Optional: disable the listener
 * @returns Scenarios with loading/error states
 */
export function useFirestoreScenarios(
  userId: string,
  enabled: boolean = true
): UseFirestoredScenariosResult {
  // Build collection path dynamically based on userId
  const collectionPath = useMemo(
    () => (userId ? `users/${userId}/scenarios` : ""),
    [userId]
  );

  const {
    data: scenariosData,
    loading,
    error,
    listening,
  } = useFirestoreCollection<FirestoreScenarioDoc>({
    collectionPath,
    constraints: SCENARIOS_CONSTRAINTS,
    transform: (doc) => {
      const data = doc.data() as Record<string, unknown> | undefined;
      if (!data) {
        return {
          id: doc.id,
          userId: "",
          name: "",
          description: "",
          settings: {} as Scenario["settings"],
          createdAt: "",
          updatedAt: "",
        } as FirestoreScenarioDoc;
      }
      return {
        id: doc.id,
        userId: data.userId,
        name: data.name,
        description: (data.description as string) || "",
        settings: data.settings,
        createdAt: data.createdAt,
        updatedAt: data.updatedAt,
      } as FirestoreScenarioDoc;
    },
    enabled: enabled && !!userId && !!collectionPath,
  });

  // Convert Firestore docs to Scenario objects
  const scenarios = useMemo<Scenario[]>(() => {
    return scenariosData.map((doc) => {
      // Parse ISO date strings to Date objects
      const createdAt =
        typeof doc.createdAt === "string"
          ? new Date(doc.createdAt)
          : doc.createdAt;
      const updatedAt =
        typeof doc.updatedAt === "string"
          ? new Date(doc.updatedAt)
          : doc.updatedAt;

      return {
        id: doc.id,
        userId: doc.userId,
        name: doc.name,
        description: doc.description,
        settings: doc.settings,
        createdAt,
        updatedAt,
      } as Scenario;
    });
  }, [scenariosData]);

  return {
    scenarios,
    loading,
    error,
    listening,
  };
}
